import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { NavNode } from "./types";

interface GuideNavProps {
  nodes: NavNode[];
  active: string;
  expanded: Record<string, boolean>;
  toggle: (id: string) => void;
  scrollTo: (id: string) => void;
  depth?: number;
}

export const GuideNav = ({ nodes, active, expanded, toggle, scrollTo, depth = 0 }: GuideNavProps) => (
  <div className={cn("space-y-0.5", depth > 0 && "ml-3 border-l border-border/50 pl-2 mt-0.5 mb-1")}>
    {nodes.map((n) => {
      const hasChildren = !!n.children?.length;
      const isOpen = !!expanded[n.id];
      const isActive = active === n.id;
      const sizeClasses = depth === 0 ? "text-sm px-2 py-1.5" : "text-sm px-2 py-1";
      return (
        <div key={n.id}>
          <div
            className={cn(
              "group flex items-center gap-1 rounded-md transition-colors",
              isActive
                ? "bg-primary-tint-soft text-primary"
                : "text-muted-foreground hover:bg-muted/50",
            )}
          >
            {hasChildren ? (
              <button
                type="button"
                onClick={() => toggle(n.id)}
                aria-label={isOpen ? "Collapse" : "Expand"}
                aria-expanded={isOpen}
                className="flex items-center justify-center h-6 w-5 rounded text-muted-foreground hover:text-foreground"
              >
                <ChevronRight
                  className={cn(
                    "h-3.5 w-3.5 transition-transform",
                    isOpen && "rotate-90",
                  )}
                />
              </button>
            ) : (
              <span className="w-5" aria-hidden />
            )}
            <button
              onClick={() => {
                if (n.groupOnly) {
                  const firstChild = n.children?.find((c) => !c.groupOnly);
                  toggle(n.id);
                  if (firstChild) scrollTo(firstChild.id);
                } else {
                  scrollTo(n.id);
                  if (hasChildren) {
                    toggle(n.id);
                  }
                }
              }}
              className={cn(
                "flex-1 text-left rounded-md transition-colors",
                sizeClasses,
                isActive
                  ? "font-semibold text-primary"
                  : "hover:text-foreground",
              )}
            >
              {n.label}
            </button>
          </div>
          {hasChildren && isOpen && (
            <GuideNav
              nodes={n.children!}
              active={active}
              expanded={expanded}
              toggle={toggle}
              scrollTo={scrollTo}
              depth={depth + 1}
            />
          )}
        </div>
      );
    })}
  </div>
);
